import React from 'react';
import { Scissors, Gauge, Download, Undo2, Redo2, Volume2, VolumeX, Trash2, Ratio, MapPin } from 'lucide-react';
import { CanvasAspect, ColorGrade, EditorSegment, ExportSettings, TimelineInsertMode } from '../../types/editor';

const SPEEDS = [0.5, 1, 1.5, 2];
const ASPECTS: CanvasAspect[] = ['16:9', '9:16', '1:1'];

interface Props {
  activeSegment: EditorSegment | null;
  canUndo: boolean;
  canRedo: boolean;
  exporting: boolean;
  exportProgress: number;
  exportSettings: ExportSettings;
  insertMode: TimelineInsertMode;
  onInsertModeChange: (mode: TimelineInsertMode) => void;
  onUndo: () => void;
  onRedo: () => void;
  onSplit: () => void;
  onDelete: () => void;
  onSpeedChange: (speed: number) => void;
  onVolumeChange: (volume: number) => void;
  onMuteToggle: () => void;
  onExport: () => void;
  onExportSettingsChange: (settings: Partial<ExportSettings>) => void;
}

export default function EditorToolbar({
  activeSegment, canUndo, canRedo, exporting, exportProgress, exportSettings,
  insertMode, onInsertModeChange,
  onUndo, onRedo, onSplit, onDelete, onSpeedChange, onVolumeChange, onMuteToggle, onExport,
  onExportSettingsChange,
}: Props) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px',
      background: 'rgba(10,10,14,0.95)', borderRadius: 12,
      border: '1px solid rgba(255,255,255,0.06)',
      flexWrap: 'wrap',
    }}>
      {/* Undo/Redo */}
      <button onClick={onUndo} disabled={!canUndo} style={tbBtn(!canUndo)} title="Desfazer">
        <Undo2 size={13} />
      </button>
      <button onClick={onRedo} disabled={!canRedo} style={tbBtn(!canRedo)} title="Refazer">
        <Redo2 size={13} />
      </button>

      <div style={dividerStyle} />

      {/* Split */}
      <button onClick={onSplit} disabled={!activeSegment} style={tbBtn(!activeSegment)} title="Dividir clip">
        <Scissors size={13} />
        <span style={{ fontSize: '0.6rem' }}>Dividir</span>
      </button>

      {/* Delete */}
      <button onClick={onDelete} disabled={!activeSegment}
        style={{ ...tbBtn(!activeSegment), ...(activeSegment ? { color: '#f87171', borderColor: 'rgba(239,68,68,0.2)' } : {}) }}
        title="Remover clip">
        <Trash2 size={12} />
      </button>

      <div style={dividerStyle} />

      {/* Speed (when segment selected) */}
      {activeSegment && (
        <>
          <Gauge size={12} style={{ color: 'rgba(255,255,255,0.4)' }} />
          <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', borderRadius: 4, padding: 1 }}>
            {SPEEDS.map(s => (
              <button key={s} onClick={() => onSpeedChange(s)} style={{
                padding: '2px 7px', borderRadius: 3, border: 'none',
                fontSize: '0.6rem', fontFamily: 'inherit',
                background: activeSegment.speed === s ? 'var(--primary)' : 'transparent',
                color: activeSegment.speed === s ? '#fff' : 'rgba(255,255,255,0.4)',
                cursor: 'pointer', fontWeight: activeSegment.speed === s ? 600 : 400,
              }}>
                {s}x
              </button>
            ))}
          </div>

          <div style={dividerStyle} />

          {/* Volume */}
          <button onClick={onMuteToggle} style={tbBtn(false)} title="Mudo">
            {activeSegment.muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
          </button>
          <input type="range" min={0} max={1} step={0.05} value={activeSegment.muted ? 0 : activeSegment.volume}
            onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
            style={{ width: 60, accentColor: '#6366f1', height: 3 }} />
          <span style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.35)', minWidth: 25 }}>
            {activeSegment.muted ? 'off' : `${Math.round(activeSegment.volume * 100)}%`}
          </span>

          <div style={dividerStyle} />
        </>
      )}

      <div style={dividerStyle} />

      <Ratio size={12} style={{ color: 'rgba(255,255,255,0.4)' }} />
      <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', borderRadius: 4, padding: 1 }}>
        {ASPECTS.map((a) => (
          <button key={a} onClick={() => onExportSettingsChange({ aspect: a })} style={{
            padding: '2px 7px', borderRadius: 3, border: 'none',
            fontSize: '0.58rem', fontFamily: 'inherit',
            background: exportSettings.aspect === a ? 'rgba(99,102,241,0.25)' : 'transparent',
            color: exportSettings.aspect === a ? '#c7d2fe' : 'rgba(255,255,255,0.4)',
            cursor: 'pointer',
          }}>
            {a}
          </button>
        ))}
      </div>

      <button
        onClick={() => onInsertModeChange(insertMode === 'end' ? 'playhead' : 'end')}
        style={tbBtn(false)}
        title="Onde novos clips entram"
      >
        <MapPin size={12} />
        <span style={{ fontSize: '0.58rem' }}>{insertMode === 'playhead' ? 'Playhead' : 'No fim'}</span>
      </button>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Export settings */}
      <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', borderRadius: 4, padding: 1 }}>
        {([
          { id: 'none' as ColorGrade, label: 'Raw' },
          { id: 'cinematic' as ColorGrade, label: 'Curva' },
        ]).map((g) => (
          <button key={g.id} onClick={() => onExportSettingsChange({ colorGrade: g.id })} style={{
            padding: '2px 7px', borderRadius: 3, border: 'none',
            fontSize: '0.58rem', fontFamily: 'inherit',
            background: exportSettings.colorGrade === g.id ? 'rgba(251,191,36,0.2)' : 'transparent',
            color: exportSettings.colorGrade === g.id ? '#fbbf24' : 'rgba(255,255,255,0.4)',
            cursor: 'pointer',
          }}>
            {g.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', borderRadius: 4, padding: 1 }}>
        {(['720p', '1080p'] as const).map(r => (
          <button key={r} onClick={() => onExportSettingsChange({ resolution: r })} style={{
            padding: '2px 7px', borderRadius: 3, border: 'none',
            fontSize: '0.58rem', fontFamily: 'inherit',
            background: exportSettings.resolution === r ? 'rgba(34,197,94,0.2)' : 'transparent',
            color: exportSettings.resolution === r ? '#4ade80' : 'rgba(255,255,255,0.4)',
            cursor: 'pointer',
          }}>
            {r}
          </button>
        ))}
      </div>

      {/* Export button */}
      <button onClick={onExport} disabled={exporting} style={{
        background: exporting ? 'rgba(34,197,94,0.1)' : 'linear-gradient(135deg, #22c55e, #16a34a)',
        border: 'none', borderRadius: 6, padding: '5px 14px',
        color: '#fff', fontSize: '0.68rem', fontWeight: 600,
        cursor: exporting ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'inherit',
        boxShadow: exporting ? 'none' : '0 2px 10px rgba(34,197,94,0.3)',
        transition: 'all 0.2s',
      }}>
        {exporting ? (
          <>
            <div style={{ width: 11, height: 11, border: '2px solid #4ade80', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            {exportProgress > 0 ? `${exportProgress}%` : 'Exportando...'}
          </>
        ) : (
          <><Download size={13} /> Exportar</>
        )}
      </button>
    </div>
  );
}

const tbBtn = (disabled: boolean): React.CSSProperties => ({
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 5, padding: '4px 8px',
  color: disabled ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.6)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'flex', alignItems: 'center', gap: 3,
  fontSize: '0.62rem', fontFamily: 'inherit',
  transition: 'all 0.15s',
});

const dividerStyle: React.CSSProperties = {
  width: 1, height: 20, background: 'rgba(255,255,255,0.06)',
};
